import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@app/components/ui/dialog";
import { Button } from "@app/components/ui/button";
import { Input } from "@app/components/ui/input";
import { Label } from "@app/components/ui/label";
import { Textarea } from "@app/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@app/components/ui/select";
import { CheckCircle2, LifeBuoy, Loader2, Send } from "lucide-react";
import { Trans, msg, useLingui } from "@lingui/macro";
import type { MessageDescriptor } from "@lingui/core";
import API from "@app/services/api";

interface ContactSupportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type Category = "support" | "feedback" | "bug" | "other";

const CATEGORY_OPTIONS: Array<{
  value: Category;
  label: MessageDescriptor;
}> = [
  { value: "support", label: msg`Demande de support` },
  { value: "feedback", label: msg`Retour d'expérience` },
  { value: "bug", label: msg`Signaler un problème` },
  { value: "other", label: msg`Autre` },
];

type Status = "idle" | "sending" | "success" | "error";

export function ContactSupportDialog({
  open,
  onOpenChange,
}: ContactSupportDialogProps) {
  const { i18n, t } = useLingui();

  const [category, setCategory] = useState<Category>("support");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState<Status>("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const resetForm = () => {
    setCategory("support");
    setSubject("");
    setMessage("");
    setStatus("idle");
    setErrorMessage(null);
  };

  const close = () => {
    if (status === "sending") return;
    resetForm();
    onOpenChange(false);
  };

  const handleSend = async () => {
    setStatus("sending");
    setErrorMessage(null);
    const res = (await API.post({
      path: "/support/contact",
      body: {
        category,
        subject: subject.trim(),
        message: message.trim(),
      },
    })) as { ok?: boolean; error?: string };

    if (res?.ok) {
      setStatus("success");
    } else {
      setStatus("error");
      setErrorMessage(res?.error || t`Une erreur est survenue lors de l'envoi.`);
    }
  };

  const canSend = message.trim().length > 0 && status !== "sending";

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(true) : close())}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <LifeBuoy className="h-5 w-5 text-primary" />
            <Trans>Aide & contact</Trans>
          </DialogTitle>
          <DialogDescription>
            <Trans>
              Une question, un retour d'expérience ou un problème ? Envoyez-nous
              un message, nous vous répondrons rapidement.
            </Trans>
          </DialogDescription>
        </DialogHeader>

        {status === "success" ? (
          <div className="flex flex-col items-center gap-3 py-6">
            <CheckCircle2 className="h-12 w-12 text-green-500" />
            <p className="text-center text-slate-700 font-medium">
              <Trans>Votre message a bien été envoyé.</Trans>
            </p>
            <p className="text-center text-slate-500 text-sm">
              <Trans>Nous reviendrons vers vous rapidement.</Trans>
            </p>
            <Button onClick={close} className="mt-2">
              <Trans>Fermer</Trans>
            </Button>
          </div>
        ) : (
          <>
            <div className="space-y-4 py-2">
              <div className="space-y-1.5">
                <Label className="text-sm font-medium text-slate-700">
                  <Trans>Type de demande</Trans>
                </Label>
                <Select
                  value={category}
                  onValueChange={(value) => setCategory(value as Category)}
                  disabled={status === "sending"}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORY_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {i18n._(opt.label)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label className="text-sm font-medium text-slate-700">
                  <Trans>Objet</Trans>
                </Label>
                <Input
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder={t`Résumé en une phrase`}
                  disabled={status === "sending"}
                />
              </div>

              <div className="space-y-1.5">
                <Label className="text-sm font-medium text-slate-700">
                  <Trans>Message</Trans>
                </Label>
                <Textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={t`Décrivez votre demande...`}
                  rows={6}
                  disabled={status === "sending"}
                />
              </div>

              {status === "error" && errorMessage && (
                <p className="text-sm text-destructive">{errorMessage}</p>
              )}
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={close}
                disabled={status === "sending"}
              >
                <Trans>Annuler</Trans>
              </Button>
              <Button onClick={handleSend} disabled={!canSend}>
                {status === "sending" ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Send className="h-4 w-4 mr-2" />
                )}
                <Trans>Envoyer</Trans>
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
